import type { FlexProps } from '@chakra-ui/react';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { parseUnits } from '@ethersproject/units';
import useDebounce from '@ui/hooks/useDebounce';
import { useStrategyPriceImpact } from '@ui/hooks/useStrategyPriceImpact';
import { useTranslations } from '@ui/i18n';
import bigNumberToNumber from '@ui/utils/bigNumberToNumber';
import type { LiquidityStrategyDepositToken } from '@ui/utils/strategy';
import { LiquidityStrategyTokenDict } from '@ui/utils/strategy';
import React, { useMemo } from 'react';

import { useDepositDrawer } from './DepositDrawer/useDepositDrawer';
import { useReceiveInfoForToken } from './useReceiveTokenInfo';
import { useStrategyStore } from './useStrategyStore';

function DepositAndWithdrawConfirmButton({
  isWithdraw,
  amountStr,
  amountInputError,
  token,
  investAmountUsd,
  ...flexProps
}: FlexProps & {
  isWithdraw: boolean;
  amountStr: string;
  amountInputError?: string;
  investAmountUsd?: number;
  token: LiquidityStrategyDepositToken;
}) {
  const t = useTranslations();
  const openDepositDrawer = useDepositDrawer((s) => s.onOpen);
  const debouncedAmountStr = useDebounce(amountStr, 500);
  const debouncedAmount = parseUnits(debouncedAmountStr || '0');
  const tokenInfo = LiquidityStrategyTokenDict[token];

  const { receivedAmountUsd } = useReceiveInfoForToken(
    token,
    debouncedAmount,
    isWithdraw,
  );

  const receivedAmountLow = useMemo(() => {
    if (receivedAmountUsd === undefined || investAmountUsd === undefined)
      return undefined;
    const receivedAmountUsdNum = bigNumberToNumber(receivedAmountUsd);
    return receivedAmountUsdNum < 0.9 * investAmountUsd;
  }, [receivedAmountUsd, investAmountUsd]);

  const priceImpact = useStrategyPriceImpact(
    token,
    debouncedAmount,
    isWithdraw,
  ).data;
  const priceImpactWarning = !!priceImpact && priceImpact < -0.05;

  return (
    <Flex flexDir="column" {...flexProps} w="full">
      <Button
        w="full"
        disabled={
          !amountStr ||
          !!amountInputError ||
          priceImpact === undefined ||
          priceImpact < -0.05 ||
          receivedAmountLow ||
          receivedAmountLow === undefined
        }
        onClick={() => {
          openDepositDrawer({
            isWithdraw,
            token,
            amountStr,
          });
          useStrategyStore.setState({
            amountStr: '',
            amountInputError: '',
          });
        }}
        mt={6}
      >
        {isWithdraw
          ? t('Withdraw {coin}', {
              coin: tokenInfo.name,
            })
          : t('Deposit {coin}', {
              coin: tokenInfo.name,
            })}
      </Button>
      <Box p="10px 8px" mt="28px" bg="brand-white" rounded="4px">
        <Text
          as="span"
          textStyle="bodyBold"
          color={priceImpactWarning || receivedAmountLow ? 'error' : 'warning'}
        >
          Attention:
        </Text>{' '}
        {priceImpactWarning
          ? t('price-impact-warning')
          : receivedAmountLow
          ? t('unbalance-warning')
          : t('liquidity-strategy-disclaimer')}
      </Box>
    </Flex>
  );
}

export default DepositAndWithdrawConfirmButton;
